import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Flex, Button, Input, Table, Thead, Tbody, Tr, Th, Td, Text, useToast, FormControl, FormLabel,
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody, Portal, useDisclosure,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
} from "@chakra-ui/react";
import { FaPlus, FaEye } from "react-icons/fa";
import { Panel } from "../../../components/shared/Section";
import { TableSkeleton } from "../../../components/shared/LoadingBlock";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import LogoCell from "./components/LogoCell";

const API = process.env.REACT_APP_API_URL;
const RETAILER_FLAGS = ["pnp", "chk", "dis", "mak", "cli", "med", "pet"];

const FlagChip = ({ on, label, onClick }) => (
  <Button
    size="xs"
    variant="ghost"
    onClick={onClick}
    aria-pressed={on}
    minW="40px"
    fontWeight="600"
    color={on ? "signal.up" : "ink.400"}
    bg={on ? "signal.upSoft" : "ink.100"}
    _hover={{ bg: on ? "signal.upSoft" : "ink.200" }}
  >
    {label}
  </Button>
);

/**
 * The Organisations view of the Users page (spec §4.3) — everything the old
 * standalone Organisations screen did (entitlement/active toggles, create,
 * org logo) plus: the org admin's sign-in creds, member/supplier counts, and a
 * View drawer listing the organisation's users and suppliers.
 */
const OrganisationsView = ({ allUsers, allSuppliers, search }) => {
  const toast = useToast();
  const { fetchOrganisations } = useSupplierRetailer();

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewOrg, setViewOrg] = useState(null); // org row for the drawer

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/get-organisations`, {});
      setOrgs(res.data?.data || []);
    } catch (error) {
      toast({ title: "Failed to load organisations", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrg = async (org) => {
    try {
      const retailers = RETAILER_FLAGS.reduce((acc, f) => ({ ...acc, [f]: !!org[f] }), {});
      await axios.post(`${API}/admin/upd-organisation`, {
        org_id: org.org_id,
        org_name: org.org_name,
        contact_email: org.contact_email,
        retailers,
        activeyn: org.activeyn,
      });
      fetchOrganisations(); // keep the top-bar toggle in step
    } catch (error) {
      toast({ title: "Failed to update organisation", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
      load(); // resync after a failed optimistic toggle
    }
  };

  const toggleFlag = (org, flag) => {
    const updated = { ...org, [flag]: !org[flag] };
    setOrgs((prev) => prev.map((o) => (o.org_id === org.org_id ? updated : o)));
    saveOrg(updated);
  };

  const q = (search || "").trim().toLowerCase();
  const rows = orgs
    .filter((o) => !q || `${o.org_code} ${o.org_name} ${o.contact_email || ""}`.toLowerCase().includes(q))
    .sort((a, b) => String(a.org_name || a.org_code).localeCompare(String(b.org_name || b.org_code), undefined, { sensitivity: "base" }));

  const orgMembers = (org) => (allUsers || []).filter((u) => u.org_code === org.org_code);
  const orgSuppliers = (org) => (allSuppliers || []).filter((s) => s.org_code === org.org_code).map((s) => s.suppliercode);

  return (
    <>
      <Panel padded={false} maxH="calc(100vh - 260px)" overflow="auto">
        {loading ? (
          <TableSkeleton rows={4} minH="160px" />
        ) : (
          <Table variant="striped" size="sm" colorScheme="custom">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th px={2} py={1} bg="gray.50">Logo</Th>
                <Th px={2} py={1} bg="gray.50">Code</Th>
                <Th px={2} py={1} bg="gray.50">Name</Th>
                <Th px={2} py={1} bg="gray.50">Org admin sign-in</Th>
                <Th px={2} py={1} bg="gray.50">Users</Th>
                <Th px={2} py={1} bg="gray.50">Suppliers</Th>
                <Th px={2} py={1} bg="gray.50">Retailers</Th>
                <Th px={2} py={1} bg="gray.50">Active</Th>
                <Th px={2} py={1} bg="gray.50">View</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((org) => {
                const admins = Array.isArray(org.org_admins) ? org.org_admins : [];
                return (
                  <Tr key={org.org_id}>
                    <Td px={2} py={1}>
                      <LogoCell storedCode={`ORG_${org.org_code}`} label={org.org_code} />
                    </Td>
                    <Td px={2} py={1} fontWeight="600">{org.org_code}</Td>
                    <Td px={2} py={1}>{org.org_name}</Td>
                    <Td px={2} py={1}>
                      {admins.length === 0 ? (
                        <Text fontSize="xs" color="ink.400">no org admin</Text>
                      ) : (
                        <Box>
                          <Text fontSize="xs">{admins[0].username} / {admins[0].userpass || "(invite pending)"}</Text>
                          {admins.length > 1 && <Text fontSize="xs" color="ink.400">+{admins.length - 1} more</Text>}
                        </Box>
                      )}
                    </Td>
                    <Td px={2} py={1}>{org.member_count}</Td>
                    <Td px={2} py={1}>{org.supplier_count}</Td>
                    <Td px={2} py={1}>
                      <Flex gap={1} wrap="wrap">
                        {RETAILER_FLAGS.map((f) => (
                          <FlagChip key={f} on={!!org[f]} label={f.toUpperCase()} onClick={() => toggleFlag(org, f)} />
                        ))}
                      </Flex>
                    </Td>
                    <Td px={2} py={1}>
                      <FlagChip on={!!org.activeyn} label={org.activeyn ? "Yes" : "No"} onClick={() => toggleFlag(org, "activeyn")} />
                    </Td>
                    <Td px={2} py={1}>
                      <Button size="xs" variant="ghost" leftIcon={<FaEye />} onClick={() => setViewOrg(org)}>View</Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Panel>

      <Drawer isOpen={!!viewOrg} placement="right" size="sm" onClose={() => setViewOrg(null)}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md">{viewOrg?.org_name} ({viewOrg?.org_code})</DrawerHeader>
          <DrawerBody>
            {viewOrg && (
              <>
                <Text fontWeight="600" fontSize="sm" mb={1}>Users</Text>
                {orgMembers(viewOrg).length === 0 && <Text fontSize="xs" color="ink.400" mb={2}>none</Text>}
                {orgMembers(viewOrg).map((u) => (
                  <Flex key={u.userid} justify="space-between" fontSize="xs" py={0.5} borderBottom="1px solid" borderColor="gray.100">
                    <Text>{u.fname}</Text>
                    <Text color="ink.500">{u.user_type === "SME" ? "Basic" : u.user_type} · {u.username}</Text>
                  </Flex>
                ))}
                <Text fontWeight="600" fontSize="sm" mt={4} mb={1}>Suppliers</Text>
                {orgSuppliers(viewOrg).length === 0 && <Text fontSize="xs" color="ink.400">none</Text>}
                {orgSuppliers(viewOrg).map((code) => (
                  <Text key={code} fontSize="xs" py={0.5} borderBottom="1px solid" borderColor="gray.100">{code}</Text>
                ))}
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

/** "Create organisation" as a popover off the toolbar (spec §4.3). */
export const CreateOrgPopover = ({ onCreated }) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newOrg, setNewOrg] = useState({ org_code: "", org_name: "", contact_email: "" });
  const [newFlags, setNewFlags] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { fetchOrganisations } = useSupplierRetailer();

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/ins-organisation`, {
        org_code: newOrg.org_code,
        org_name: newOrg.org_name,
        contact_email: newOrg.contact_email,
        retailers: newFlags,
      });
      toast({ title: res.data.message, status: "success", duration: 3000, isClosable: true });
      setNewOrg({ org_code: "", org_name: "", contact_email: "" });
      setNewFlags({});
      fetchOrganisations();
      onClose();
      if (onCreated) onCreated();
    } catch (error) {
      toast({ title: "Failed to create organisation", description: error?.response?.data?.message, status: "error", duration: 8000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Button leftIcon={<FaPlus />}>Create organisation</Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="360px">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontSize="sm" fontWeight="600">New organisation</PopoverHeader>
          <PopoverBody>
            <FormControl isRequired mb={2}>
              <FormLabel fontSize="xs" mb={1}>Code</FormLabel>
              <Input size="sm" value={newOrg.org_code} onChange={(e) => setNewOrg({ ...newOrg, org_code: e.target.value.toUpperCase() })} placeholder="ie: GATEWAY" />
            </FormControl>
            <FormControl isRequired mb={2}>
              <FormLabel fontSize="xs" mb={1}>Name</FormLabel>
              <Input size="sm" value={newOrg.org_name} onChange={(e) => setNewOrg({ ...newOrg, org_name: e.target.value })} placeholder="ie: Gateway" />
            </FormControl>
            <FormControl mb={2}>
              <FormLabel fontSize="xs" mb={1}>Contact email</FormLabel>
              <Input size="sm" type="email" value={newOrg.contact_email} onChange={(e) => setNewOrg({ ...newOrg, contact_email: e.target.value })} placeholder="optional" />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="xs" mb={1}>Retailers</FormLabel>
              <Flex gap={1} wrap="wrap">
                {RETAILER_FLAGS.map((f) => (
                  <FlagChip key={f} on={!!newFlags[f]} label={f.toUpperCase()} onClick={() => setNewFlags((prev) => ({ ...prev, [f]: !prev[f] }))} />
                ))}
              </Flex>
            </FormControl>
            <Flex justify="flex-end">
              <Button size="sm" leftIcon={<FaPlus />} isLoading={submitting} onClick={handleCreate}>Create</Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default OrganisationsView;
